import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { theme } from "./theme";
import {
  AtmosphericParticles,
  DynamicSubtitle,
  FilmGrain,
  SpecularShine,
} from "./components";

export const KyuStoreCinematicTactical: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  // Helper spring shortcut
  const sp = (delay: number, damping = 200, stiffness = 120) =>
    spring({
      frame,
      fps,
      delay,
      config: { damping, stiffness },
    });

  // Dynamic Audio Ducking (Remotion Pro pattern)
  const bgmVolume = (f: number) => {
    const fadeIn = interpolate(f, [0, 20], [0, 1], { extrapolateRight: "clamp" });
    const fadeOut = interpolate(f, [durationInFrames - 30, durationInFrames], [1, 0], {
      extrapolateLeft: "clamp",
    });
    const duck = f < 490 ? 0.12 : 0.45;
    return duck * Math.min(fadeIn, fadeOut);
  };

  // Subtitles pinned at safe band bottom: 400px
  const SUBTITLES = [
    { from: 0, to: 65, text: "Bukan sulap, bukan trik.", highlight: "Bukan sulap, bukan trik" },
    { from: 65, to: 215, text: "Di Kyu Store, diamond Mobile Legends masuk ke akunmu dalam nol koma delapan detik!", highlight: "dalam nol koma delapan detik!" },
    { from: 215, to: 355, text: "Seratus persen resmi Moonton, anti-minus, dan tanpa minta password.", highlight: "resmi Moonton, anti-minus" },
    { from: 355, to: 420, text: "Mau harga Weekly Pass paling hemat?", highlight: "harga Weekly Pass paling hemat?" },
    { from: 420, to: 540, text: "Buka kyustore.com sekarang juga!", highlight: "Buka kyustore.com sekarang juga!" },
  ];

  // =========================================================================
  // 3D & CINEMATIC TECHNIQUES:
  // 1. Continuous Camera Drift (never static for >1s)
  // 2. Impact Frames & Zoom Punch on the 0.82s hit (Frames 136 - 146)
  // 3. Anticipation Dip on CTA (Frames 430 - 450)
  // =========================================================================

  // 1. Continuous Organic Camera Drift
  const driftY = Math.sin(frame / 32) * 2.8;
  const driftX = Math.cos(frame / 38) * 1.8;

  // Scene transitions
  const camSceneRotY = interpolate(frame, [0, 70, 215, 420, 540], [-4, 5, -5, 4, 0], {
    easing: theme.ease,
  });
  const camSceneRotX = interpolate(frame, [0, 70, 215, 420, 540], [5, 2, 4, 2, 0], {
    easing: theme.ease,
  });

  const totalRotY = camSceneRotY + driftY;
  const totalRotX = camSceneRotX + driftX;

  // 2. Impact Frame Shake & Zoom Punch on Frame 136 - 144
  const isImpact = frame >= 136 && frame <= 144;
  const impactShakeX = isImpact ? (frame % 2 === 0 ? 10 : -10) : 0;
  const impactShakeY = isImpact ? (frame % 3 === 0 ? -6 : 6) : 0;
  const zoomPunch = interpolate(frame, [134, 137, 150], [1, 1.07, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: theme.ease,
  });

  // 3. Anticipation Dip on Button / Cards
  const ctaSpring = sp(430, 200);
  const ctaDip = interpolate(frame, [424, 429, 440], [0, 8, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const ctaPulse = Math.sin((frame - 460) * 0.18) * 0.03 + 1.0;

  // Specular Shine
  const shineProgress = interpolate(frame % 180, [0, 50], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: theme.ease,
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: theme.colors.bg,
        fontFamily: theme.fonts.headline,
        color: theme.colors.text,
        overflow: "hidden",
      }}
    >
      {/* Procedural Film Grain Overlay from Remotion 3D and Cinematic */}
      <FilmGrain opacity={0.045} />

      {/* Audio Engine with Pro Dynamic Ducking */}
      <Audio src={staticFile("kyustore_tactical_mastered.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={bgmVolume} />

      {/* Layered SFX Cues */}
      {frame === 4 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.65} />}
      {frame === 68 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.7} />}
      {frame === 136 && <Audio src={staticFile("ui-success.mp3")} volume={0.9} />}
      {frame === 137 && <Audio src={staticFile("chime.mp3")} volume={0.85} />}
      {frame === 215 && <Audio src={staticFile("pop.mp3")} volume={0.7} />}
      {frame === 280 && <Audio src={staticFile("pop.mp3")} volume={0.7} />}
      {frame === 425 && <Audio src={staticFile("whoosh.mp3")} volume={0.75} />}
      {frame === 450 && <Audio src={staticFile("click.mp3")} volume={0.75} />}

      {/* Background Subtle Tech Grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(rgba(93, 63, 211, 0.08) 1.5px, transparent 1.5px),
            linear-gradient(to right, rgba(93, 63, 211, 0.035) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px, 64px 64px",
          pointerEvents: "none",
        }}
      />

      {/* Ambient Spotlight */}
      <div
        style={{
          position: "absolute",
          top: "45%",
          left: "50%",
          width: 900,
          height: 900,
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, rgba(93, 63, 211, 0.15) 0%, rgba(248, 246, 254, 0) 70%)",
          filter: "blur(90px)",
          pointerEvents: "none",
        }}
      />

      {/* Top Header HUD Bar */}
      <div
        style={{
          position: "absolute",
          top: 80,
          left: theme.spacing.page,
          right: theme.spacing.page,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Img src={staticFile("kyu_logo.png")} style={{ height: 48, objectFit: "contain" }} />
          <div style={{ width: 2, height: 26, backgroundColor: "#CBD5E1" }} />
          <span
            style={{
              fontFamily: theme.fonts.technical,
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "0.12em",
              color: theme.colors.muted,
            }}
          >
            [ TACTICAL SPEED SHOWCASE ]
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            backgroundColor: theme.colors.surface,
            border: `2px solid ${theme.colors.success}`,
            padding: "8px 22px",
            borderRadius: 24,
            boxShadow: "0 4px 15px rgba(16, 185, 129, 0.15)",
          }}
        >
          <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: theme.colors.success, boxShadow: "0 0 10px #10B981" }} />
          <span
            style={{
              fontFamily: theme.fonts.technical,
              fontSize: 18,
              fontWeight: 700,
              color: theme.colors.success,
              letterSpacing: "0.08em",
            }}
          >
            OFFICIAL MOONTON DIRECT API
          </span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 3D PERSPECTIVE VIEWPORT (1400px) with IMPACT SHAKE & ZOOM PUNCH           */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          perspective: 1400,
          perspectiveOrigin: "50% 48%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: `translate(${impactShakeX}px, ${impactShakeY}px) scale(${zoomPunch})`,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "relative",
            width: 900,
            height: 1100,
            transformStyle: "preserve-3d",
            transform: `rotateY(${totalRotY}deg) rotateX(${totalRotX}deg)`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >

          {/* ===================================================================== */}
          {/* ACT 1: HOOK & SCALE CONTRAST (Frames 0 - 215)                         */}
          {/* Scale Contrast Technique: Colossal 200px Number vs Clean Supporting    */}
          {/* ===================================================================== */}
          {frame < 220 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(frame, [200, 218], [1, 0], { extrapolateLeft: "clamp" }),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              {/* Eyebrow Hook Tag */}
              <div
                style={{
                  backgroundColor: theme.colors.accentLight,
                  border: `2px solid ${theme.colors.accent}`,
                  borderRadius: 24,
                  padding: "8px 28px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 16,
                  boxShadow: "0 6px 20px rgba(93, 63, 211, 0.18)",
                  transform: `scale(${interpolate(sp(4, 200), [0, 1], [0.8, 1])})`,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.colors.accent} strokeWidth="3">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 800, color: theme.colors.accent, letterSpacing: "0.08em" }}>
                  BUKAN SULAP • BUKAN TRIK
                </span>
              </div>

              {/* Headline */}
              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 72,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 20,
                  transform: `scale(${interpolate(sp(12, 200), [0, 1], [0.85, 1])})`,
                }}
              >
                DIAMOND MASUK KE AKUNMU <br />
                <span style={{ color: theme.colors.accent }}>HANYA DALAM HITUNGAN DETIK</span>
              </h1>

              {/* Master 3D Stage with Colossal Scale Contrast Number */}
              <div
                style={{
                  position: "relative",
                  width: 840,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "36px 40px",
                  transformStyle: "preserve-3d",
                  transform: `scale(${interpolate(sp(20, 200), [0, 1], [0.88, 1])})`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Sub-label */}
                <div
                  style={{
                    fontFamily: theme.fonts.technical,
                    fontSize: 20,
                    fontWeight: 700,
                    color: theme.colors.muted,
                    letterSpacing: "0.15em",
                    marginBottom: 4,
                    transform: "translateZ(30px)",
                  }}
                >
                  DURASI PROSES INJECT MOONTON REALTIME
                </div>

                {/* ================================================================= */}
                {/* SCALE CONTRAST HERO: GIANT 190px NUMBER (Bebas Neue)             */}
                {/* ================================================================= */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 16,
                    transform: "translateZ(65px)",
                    margin: "8px 0",
                  }}
                >
                  <span
                    style={{
                      fontFamily: theme.fonts.display,
                      fontSize: 190,
                      lineHeight: 0.85,
                      color: theme.colors.success,
                      fontVariantNumeric: "tabular-nums",
                      textShadow: "0 15px 45px rgba(16, 185, 129, 0.35)",
                      letterSpacing: "0.02em",
                    }}
                  >
                    0.82
                  </span>
                  <span
                    style={{
                      fontFamily: theme.fonts.display,
                      fontSize: 80,
                      color: theme.colors.text,
                      letterSpacing: "0.04em",
                    }}
                  >
                    DETIK
                  </span>
                </div>

                {/* Supporting Info Bar inside Card */}
                <div
                  style={{
                    width: "100%",
                    backgroundColor: theme.colors.darkBadge,
                    color: "#FFFFFF",
                    borderRadius: 22,
                    padding: "16px 28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transform: "translateZ(45px)",
                    marginTop: 14,
                    boxShadow: "0 15px 35px rgba(30, 18, 74, 0.35)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <Img src={staticFile("diamond_3d_crystal.png")} style={{ width: 48, height: 48, objectFit: "contain" }} />
                    <span style={{ fontFamily: theme.fonts.display, fontSize: 32, letterSpacing: "0.02em" }}>
                      HASIL TRANSAKSI TERKONFIRMASI DI GAME
                    </span>
                  </div>
                  <div style={{ fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 800, color: theme.colors.success }}>
                    100% SUKSES
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* ACT 2: BREAK THE CENTER — SPLIT VIEWPORT (Frames 215 - 425)           */}
          {/* ===================================================================== */}
          {frame >= 210 && frame < 430 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(
                  frame,
                  [210, 225, 410, 428],
                  [0, 1, 1, 0],
                  { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
                ),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              {/* Header Badge */}
              <div
                style={{
                  backgroundColor: theme.colors.successLight,
                  border: `2px solid ${theme.colors.success}`,
                  padding: "6px 24px",
                  borderRadius: 22,
                  marginBottom: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(16, 185, 129, 0.15)",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.success, letterSpacing: "0.08em" }}>
                  KEAMANAN TINGKAT TINGGI & HARGA HEMAT
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 72,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 20,
                }}
              >
                100% RESMI MOONTON, <span style={{ color: theme.colors.accent }}>ANTI-MINUS & TANPA PASSWORD</span>
              </h1>

              {/* Break the Center: Asymmetrical Split Stage */}
              <div
                style={{
                  position: "relative",
                  width: 860,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "32px 36px",
                  transformStyle: "preserve-3d",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 28,
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Left Side: 3D Weekly Diamond Pass Hero (z: 55px) */}
                <div
                  style={{
                    flex: "0 0 340px",
                    height: "100%",
                    backgroundColor: theme.colors.surface,
                    borderRadius: 28,
                    border: "2px solid #E2E8F0",
                    padding: "24px 20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: "translateZ(55px)",
                    boxShadow: "0 15px 35px rgba(0,0,0,0.06)",
                    position: "relative",
                  }}
                >
                  <Img
                    src={staticFile("official_weekly_pass_clean.png")}
                    style={{
                      width: 200,
                      height: 200,
                      objectFit: "contain",
                      filter: "drop-shadow(0 15px 25px rgba(93, 63, 211, 0.3))",
                      marginBottom: 12,
                    }}
                  />
                  <div style={{ fontFamily: theme.fonts.display, fontSize: 32, color: theme.colors.text }}>
                    WEEKLY DIAMOND PASS
                  </div>
                  <div style={{ fontFamily: theme.fonts.display, fontSize: 48, color: theme.colors.accent, lineHeight: 0.95, margin: "6px 0" }}>
                    RP 25.000
                  </div>
                  <div style={{ fontFamily: theme.fonts.technical, fontSize: 14, fontWeight: 700, color: theme.colors.success }}>
                    HARGA FLASH SALE RESMI
                  </div>
                </div>

                {/* Right Side: Staggered Security Pillars (z: 35px) */}
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    transform: "translateZ(35px)",
                  }}
                >
                  {/* Point 1: No Password */}
                  <div
                    style={{
                      backgroundColor: theme.colors.surface,
                      borderRadius: 20,
                      border: `2px solid ${theme.colors.success}`,
                      padding: "16px 20px",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      boxShadow: "0 6px 20px rgba(16, 185, 129, 0.12)",
                    }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: theme.colors.successLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: theme.colors.success }}>
                        PRIVASI TERJAMIN
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: theme.colors.text }}>
                        TANPA LOGIN & PASSWORD
                      </div>
                    </div>
                  </div>

                  {/* Point 2: Anti-Minus Guarantee */}
                  <div
                    style={{
                      backgroundColor: theme.colors.surface,
                      borderRadius: 20,
                      border: "2px solid #E2E8F0",
                      padding: "16px 20px",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: theme.colors.accentLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={theme.colors.accent} strokeWidth="3">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: theme.colors.accent }}>
                        GARANSI AKUN
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: theme.colors.text }}>
                        100% BEBAS BANNED MOONTON
                      </div>
                    </div>
                  </div>

                  {/* Point 3: Direct API QRIS */}
                  <div
                    style={{
                      backgroundColor: theme.colors.darkBadge,
                      color: "#FFFFFF",
                      borderRadius: 20,
                      padding: "16px 20px",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      boxShadow: "0 10px 25px rgba(30, 18, 74, 0.3)",
                    }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: "rgba(16, 185, 129, 0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: "#00F0FF" }}>
                        PEMBAYARAN RESMI
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: "#FFFFFF" }}>
                        SCAN QRIS OTOMATIS TERVERIFIKASI
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* ACT 3: RESOLUTION & FINAL CTA (Frames 420 - 540)                       */}
          {/* Features Anticipation Dip + 45-frame Final Hold                       */}
          {/* ===================================================================== */}
          {frame >= 415 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(frame, [415, 430], [0, 1], { extrapolateLeft: "clamp" }),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: theme.colors.successLight,
                  border: `2px solid ${theme.colors.success}`,
                  padding: "6px 24px",
                  borderRadius: 22,
                  marginBottom: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(16, 185, 129, 0.15)",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.success, letterSpacing: "0.08em" }}>
                  HARGA RESMI • PROSES SATSET
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 74,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 24,
                }}
              >
                BUKA SEKARANG <span style={{ color: theme.colors.accent }}>DI KYUSTORE.COM</span>
              </h1>

              {/* Master CTA Card with Anticipation Dip */}
              <div
                style={{
                  position: "relative",
                  width: 840,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "40px",
                  transformStyle: "preserve-3d",
                  transform: `scale(${interpolate(ctaSpring, [0, 1], [0.85, 1])}) translateY(${ctaDip}px)`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Big Kyu Store Brand Unit */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 20,
                    marginBottom: 26,
                    transform: "translateZ(35px)",
                  }}
                >
                  <Img
                    src={staticFile("kyu_logo.png")}
                    style={{
                      height: 110,
                      objectFit: "contain",
                      filter: "drop-shadow(0 12px 28px rgba(93, 63, 211, 0.28))",
                    }}
                  />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontFamily: theme.fonts.display, fontSize: 56, color: theme.colors.text, lineHeight: 0.95 }}>
                      KYU STORE
                    </span>
                    <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.success, letterSpacing: "0.1em" }}>
                      DISTRIBUTOR RESMI MOONTON INDONESIA
                    </span>
                  </div>
                </div>

                {/* Big Glowing CTA Button with Pulse */}
                <div
                  style={{
                    backgroundColor: theme.colors.success,
                    color: "#FFFFFF",
                    padding: "22px 54px",
                    borderRadius: 26,
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    boxShadow: "0 18px 45px rgba(16, 185, 129, 0.38)",
                    transform: `translateZ(50px) scale(${ctaPulse})`,
                    marginBottom: 26,
                  }}
                >
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span style={{ fontFamily: theme.fonts.display, fontSize: 42, letterSpacing: "0.04em" }}>
                    TOP UP SEKARANG: KYUSTORE.COM
                  </span>
                </div>

                {/* Trust Points */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 32,
                    fontFamily: theme.fonts.headline,
                    fontSize: 20,
                    fontWeight: 800,
                    color: theme.colors.muted,
                    transform: "translateZ(30px)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>100% Legal & Aman</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>0.82 Detik Satset</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Cukup User ID & Zone</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 18 Atmospheric Floating Light Motes */}
          <AtmosphericParticles frame={frame} count={18} />
        </div>
      </div>

      {/* ========================================================================= */}
      {/* FOOTER TICKER (Safe Zone Bottom 80px)                                     */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          bottom: 80,
          left: theme.spacing.page,
          right: theme.spacing.page,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: theme.colors.surface,
          border: `2px solid ${theme.colors.border}`,
          borderRadius: 22,
          padding: "14px 30px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, fontFamily: theme.fonts.headline, fontSize: 18, fontWeight: 800, color: theme.colors.muted }}>
          <span style={{ color: theme.colors.accent }}>METODE RESMI:</span>
          <span>QRIS • BCA • MANDIRI • BRI • DANA • GOPAY • SHOPEEPAY</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 700, color: theme.colors.success }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>AUTO INJECT ACTIVE 24/7</span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* DYNAMIC SUBTITLES: PINNED AT OPTIMAL SAFE BAND (bottom: 400px)            */}
      {/* ========================================================================= */}
      <DynamicSubtitle frame={frame} subtitles={SUBTITLES} bottom={400} />
    </AbsoluteFill>
  );
};
